import { useAuthenticatedAxiosInstance } from './useAuthenticatedAxiosInstance';
import { useCallback } from 'react';
import { AxiosResponse } from 'axios';
import { getEnvironmentConfig } from '../environmentConfig';
import type { CallIntegrationRequest, CallIntegrationResponse, IntegrationAccess } from '@glueapp/graph-types';
function isValidURL(url: string): boolean {
    try {
        const parsedURL = new URL(url);
        return true;
    } catch (error) {
        return false;
    }
}

export const useIntegrationAccessApi = () => {
    const api = useAuthenticatedAxiosInstance(`${getEnvironmentConfig().integrationServiceUrl}`);

    const getAllIntegrationAccesses = useCallback(async (): Promise<IntegrationAccess[]> => {
        const result: AxiosResponse = await api.get(`/integrations`);
        return result.data.integrationAccesses;
    }, [api]);

    const deleteIntegrationAccess = useCallback(
        async (integrationAccess: IntegrationAccess): Promise<void> => {
            await api.delete(`/integrations/${integrationAccess.id}`);
        },
        [api],
    );

    const createOktaIntegrationAccess = useCallback(
        async (oktaInformation: { clientId: string; clientSecret: string; oktaDomain: string }): Promise<void> => {
            await api.post(`/integrations/okta`, oktaInformation);
        },
        [api],
    );

    const callIntegration = useCallback(
        async <T>(integrationAccess: IntegrationAccess, requestConfig: CallIntegrationRequest): Promise<CallIntegrationResponse<T>> => {
            const { url: callUrl, baseURL: callBaseUrl, ...remainingRequestConfig } = requestConfig;
            const params = new URLSearchParams();
            if (!callUrl) {
                throw new Error(`Node using Integration: ${integrationAccess.provider} called callIntegration without a URL`);
            }
            if (callBaseUrl) {
                const url = new URL(callUrl, callBaseUrl);

                for (const param of Object.entries((requestConfig.params as Record<string, any>) ?? {})) {
                    url.searchParams.append(param[0], param[1]);
                }

                params.append('integrationUrl', url.toString());
            } else {
                if (isValidURL(callUrl)) {
                    const url = new URL(callUrl);

                    for (const param of Object.entries((requestConfig.params as Record<string, any>) ?? {})) {
                        url.searchParams.append(param[0], param[1]);
                    }

                    params.append('integrationUrl', url.toString());
                } else {
                    const url = new URL(callUrl, 'https://placeholder.glueapp.org');

                    for (const param of Object.entries((requestConfig.params as Record<string, any>) ?? {})) {
                        url.searchParams.append(param[0], param[1]);
                    }

                    params.append('integrationUrl', url.toString().replace('https://placeholder.glueapp.org', ''));
                }
            }
            const response = await api.request({
                ...remainingRequestConfig,
                url: `/integrations/${integrationAccess.id}/call`,
                params: params,
            });
            return {
                data: response.data,
                status: response.status,
            };
        },
        [api],
    );

    return {
        getAllIntegrationAccesses,
        deleteIntegrationAccess,
        callIntegration,
        createOktaIntegrationAccess,
    };
};
